<template>
  <div ref="focusTrap" class="flex items-center justify-center">
    <button
      @click="openMenu"
      class="h-8 w-8 flex-shrink-0"
      type="button"
      :aria-label="isOpen ? 'open-menu' : 'close-menu'"
    >
      <MenuIcon v-if="!isOpen" class="w-full text-white" />
      <CloseIcon v-else class="w-full h-full p-[7px] text-white" />
    </button>
    <div
      class="fixed z-[999] left-0 right-0 top-[65px] min-[768px]:top-[73px] bottom-0 w-screen text-[#3C80DF]"
      role="dialog"
      v-if="isOpen"
      ref="target"
    >
      <div
        v-if="!isTreatmentsMenuOpen"
        class="flex flex-col gap-6 items-start text-lg h-full w-full bg-white overflow-y-auto mx-auto p-6 min-[768px]:px-12 pb-10"
      >
        <button
          type="button"
          @click="toggleTreatmentsMenu"
          class="w-full p-4 flex justify-between bg-[#EDF0FA] rounded-xl font-bold items-center"
        >
          Browse all treatments
          <ChevronRight class="w-4 h-4 mr-[6px] text-[#3C80DF]" />
        </button>
        <ul class="w-full flex flex-col space-y-2">
          <li v-for="item in props.content.secondaryNavigationLinks" :key="item.label" class="w-full">
            <a :href="item.href" class="w-full px-4 py-[10px] flex justify-between items-center rounded-xl">
              {{ item.label }}
              <ChevronRight class="w-4 h-4 mr-[6px] text-[#3C80DF]" />
            </a>
          </li>
        </ul>
        <a
          href="/account-dashboard"
          class="mt-auto w-full bg-[#FA650A] text-white rounded-full p-4 font-bold flex items-center justify-center text-base"
        >
          <AccountCircle class="w-5 h-5 flex-shrink-0 mr-[10px]" />
          My account
        </a>
      </div>
      <div v-else class="flex flex-col h-full z-[999] bg-white overflow-y-auto p-6 pb-10 min-[768px]:px-12 gap-y-6">
        <button type="button" @click="toggleTreatmentsMenu" class="text-lg flex items-center w-full">
          <ChevronLeft class="w-4 h-4 text-[#3C80DF] mr-2" />
          Back
        </button>
        <div v-for="item in props.content.primaryExpandedContent.items" :key="item.label" class="text-[#003F82]">
          <a
            v-if="item.href"
            :href="item.href"
            class="relative block text-lg font-bold after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-[2px] after:w-full after:bg-[#003F82] after:content-[''] hover:no-underline"
            >{{ item.label }}</a
          >
          <p
            v-else
            class="relative pointer-events-none text-lg font-bold after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-[2px] after:w-full after:bg-[#003F82] after:content-[''] hover:no-underline"
          >
            {{ item.label }}
          </p>
          <ul class="mt-5 space-y-2" v-if="isNavigationCategoryItem(item)">
            <li v-for="secondaryItem in item.items" :key="secondaryItem.label">
              <a :href="secondaryItem.href" class="text-[#3C80DF] hover:underline">
                {{ secondaryItem.label }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import MenuIcon from "../../../resources/menu.svg?inline";
import CloseIcon from "../../../resources/close.svg?inline";
import ChevronRight from "../../../resources/chevron-right.svg?inline";
import ChevronLeft from "../../../resources/chevron-left.svg?inline";
import AccountCircle from "../../../resources/account-circle.svg?inline";
import { type DRXNavigationContent, isNavigationCategoryItem } from "../types";
import { nextTick, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";

const props = defineProps<{
  content: DRXNavigationContent;
}>();

const target = ref(null);

const focusTrap = ref(null);

const isOpen = ref(false);

const isTreatmentsMenuOpen = ref(false);

const { activate, deactivate } = useFocusTrap(focusTrap);

const openMenu = async () => {
  // hide zendesk chat widget
  try {
    (window as any).zE["hide"]();
  } catch (e) {}

  // hide hotjar survey
  const survey = document.querySelector("_hj-widget-container");
  if (survey) survey.setAttribute("style", "display: none !important");

  isOpen.value = true;
  if (isOpen.value) document.documentElement.style.setProperty("overflow-y", "hidden");

  if (isOpen.value) {
    await nextTick();
    activate();
  } else {
    await nextTick();
    deactivate();
  }
};

const toggleTreatmentsMenu = () => {
  isTreatmentsMenuOpen.value = !isTreatmentsMenuOpen.value;
};

onClickOutside(target, (event) => {
  if (isOpen.value) {
    event.stopPropagation();
    isOpen.value = false;
    if (isTreatmentsMenuOpen.value) {
      isTreatmentsMenuOpen.value = false;
    }
    document.documentElement.style.removeProperty("overflow-y");
    deactivate();
  }

  // show zendesk chat widget
  try {
    (window as any).zE["show"]();
  } catch (e) {}

  // show hotjar survey
  const survey = document.querySelector("_hj-widget-container");
  if (survey) survey.setAttribute("style", "display: block !important");
});
</script>

<style scoped>
@tailwind base;
@tailwind components;
@tailwind utilities;
</style>

<style lang="scss" scoped>
@import "@theme/Main.scss";
</style>
